import { inject, Injectable } from '@angular/core';
import dayjs from 'dayjs';
import { difference, groupBy, pickBy, round, uniqBy } from 'lodash-es';
import { BehaviorSubject, firstValueFrom, forkJoin, pipe } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { ExtendedBudgetData } from '@models/budget/budget.model';
import { BudgetStore } from '@models/budget/budget.store';

import {
  BudgetSnapshotType,
  BudgetType,
  BudgetViewInput,
  ExpenseType,
  GqlService,
  listBudgetGridV2Query,
  listBudgetVersionsQuery,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { BudgetCurrencyQuery } from '@shared/store/budget-currency/budget-currency.query';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { Option } from '@shared/types/components.type';
import { Utils } from '@shared/utils/utils';

import { BudgetDataArrayType, BudgetGridService } from './budget-grid.service';
import { BudgetService } from './budget.service';

const NOT_RESETTABLE_GRID_KEYS: (
  | 'display_group'
  | 'item_group'
  | 'vendor_name'
  | keyof ExtendedBudgetData
)[] = [
  'activity_id',
  'activity_name',
  'cost_category',
  'cost_category_ordering',
  'vendor_id',
  'group_index',
  'display_group',
  'item_group',
  'vendor_name',
  'group0',
  'group1',
  'group2',
  'group3',
  'group4',
];

@Injectable({
  providedIn: 'root',
})
export class SnapshotService {
  private budgetCurrencyQuery = inject(BudgetCurrencyQuery);
  private budgetGridService = inject(BudgetGridService);
  private budgetService = inject(BudgetService);
  private budgetStore = inject(BudgetStore);
  private gqlService = inject(GqlService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);
  private vendorQuery = inject(OrganizationQuery);

  budgetVersions$ = new BehaviorSubject<listBudgetVersionsQuery[]>([]);
  loading$ = new BehaviorSubject(false);

  private calculateActuals(budgetRow: ExtendedBudgetData, keyPrefix = ''): ExtendedBudgetData {
    return Object.keys(budgetRow)
      .filter((key) => /^(EXPENSE_WP|EXPENSE_FORECAST)::\w*-\d{4}$/.test(key))
      .reduce(
        (accum, key) => {
          const date = dayjs(key);

          if (!date.isValid()) {
            return accum;
          }

          const isForecastKey = key.startsWith('EXPENSE_FORECAST::');
          const keyAffix = isForecastKey ? 'EXPENSE_FORECAST::' : 'EXPENSE_WP::';

          const yearKey = `${keyAffix}${date.year()}`;
          const quarter = Math.floor(date.month() / 3) + 1;
          const quarterKey = `${keyAffix}Q${quarter}-${date.year()}${keyPrefix}`;

          const yearActuals =
            (<number>accum[`${yearKey}${keyPrefix}`] || 0) + <number>budgetRow[key];

          return <ExtendedBudgetData>{
            ...accum,
            [`${yearKey}${keyPrefix}`]: yearActuals,
            [quarterKey]: (<number>accum[quarterKey] || 0) + <number>budgetRow[key],
          };
        },
        <ExtendedBudgetData>{}
      );
  }

  findExpenseAmounts(
    snapshotData: ExtendedBudgetData,
    period: string,
    actualsPriority: ExpenseType[]
  ) {
    let snapshotActual;
    let snapshotActualUSD;

    for (const expenseType of actualsPriority) {
      snapshotActual = (snapshotData?.expenses || []).find(
        (expense) => expense.period === period && expense.expense_type === expenseType
      )?.contract_amount;
      snapshotActualUSD = (snapshotData?.expenses || []).find(
        (expense) => expense.period === period && expense.expense_type === expenseType
      )?.amount;

      if (snapshotActual != null) {
        break;
      }
    }

    return { actual: snapshotActual, actualUSD: snapshotActualUSD };
  }

  getBudgetSnapshots(snapshotName: string) {
    const inputs = this.getSnapshotInputs(snapshotName);

    this.budgetStore.setLoading(true);

    return forkJoin(inputs.map((input) => this.gqlService.listBudgetGridV2$(input))).pipe(
      this.budgetService.budgetsCacheMechanism(),
      map((gridDataList: listBudgetGridV2Query[]) => {
        const isAllSuccess = gridDataList.length ? gridDataList.every((data) => !!data) : false;

        const tableData = gridDataList
          .map((data) => data?.budget_data || [])
          .reduce<BudgetDataArrayType>((accum, budgetVendor) => {
            accum = accum?.concat(budgetVendor);

            return accum;
          }, []);

        if (isAllSuccess && tableData?.length) {
          const vendors = this.vendorQuery.getAllVendors();

          const budgetGridData = this.budgetStore.getValue().current_data;
          const sortedVendorIds = this.getVendorIdList(budgetGridData);
          const snapshotVendorIds = this.getVendorIdList(tableData);

          const budget_data: ExtendedBudgetData[] =
            this.budgetGridService.getBudgetGridWithBaseLineForVendorsV2(
              tableData as ExtendedBudgetData[],
              vendors,
              this.budgetCurrencyQuery.getValue().currency
            );

          const vendorDifferences = difference(sortedVendorIds, snapshotVendorIds);
          const vendorsToReset: string[] = [];

          vendorDifferences.forEach((diff) => {
            if (!sortedVendorIds.includes(diff)) {
              vendorsToReset.push(diff);
            }
          });

          this.budgetStore.update({
            budget_data: this.resetInvalidBudgets(
              this.getSnapshotBudget(budget_data, budgetGridData),
              vendorsToReset
            ),
          });
        }

        this.budgetStore.setLoading(false);
      })
    );
  }

  private getExtendedBudgetDataKey(data: ExtendedBudgetData) {
    return data.activity_id
      ? data.activity_id
      : `${data.vendor_id}|${data.cost_category?.toLowerCase()}`;
  }

  private getMonthsSnapshotActuals(
    snapshotData: ExtendedBudgetData,
    budgetGridData: ExtendedBudgetData
  ) {
    return Object.keys(budgetGridData)
      .filter((key) => /^EXPENSE_WP::\w*-\d{4}$/.test(key))
      .reduce((accum, monthKey) => {
        const period = monthKey.replace('EXPENSE_WP::', '');
        const forecastMonthKey = monthKey.replace('EXPENSE_WP::', 'EXPENSE_FORECAST::');

        const actualsPriority = [ExpenseType.EXPENSE_WP, ExpenseType.EXPENSE_FORECAST];

        let { actual: snapshotActual, actualUSD: snapshotActualUSD } = this.findExpenseAmounts(
          snapshotData || [],
          period,
          actualsPriority
        );

        snapshotActual ||= 0;
        snapshotActualUSD ||= 0;

        return {
          ...accum,
          [forecastMonthKey]: 0,
          [forecastMonthKey.replace('EXPENSE_FORECAST', 'EXPENSE_FORECAST_USD')]: 0,
          [monthKey]: snapshotActual,
          [monthKey.replace('EXPENSE_WP', 'EXPENSE_WP_USD')]: snapshotActualUSD,
        };
      }, {});
  }

  private getSnapshotBudget(
    snapshotData: ExtendedBudgetData[],
    budgetGridData: ExtendedBudgetData[]
  ): ExtendedBudgetData[] {
    const snapshotGroupedValues = groupBy(snapshotData, this.getExtendedBudgetDataKey);

    const currentBudgetGroupedValues = groupBy(budgetGridData, this.getExtendedBudgetDataKey);

    return Object.entries(currentBudgetGroupedValues)
      .map(([dataKey, currentBudgetRow]) => {
        return snapshotGroupedValues[dataKey]
          ? snapshotGroupedValues[dataKey]
          : currentBudgetRow.map(this.resetGridRow);
      })
      .reduce<ExtendedBudgetData[]>((accum, row) => {
        accum = accum.concat(row);

        return accum;
      }, [])
      .map((budget) => {
        const dataKey = this.getExtendedBudgetDataKey(budget);
        const currentBudgetRow = Array.isArray(currentBudgetGroupedValues[dataKey])
          ? currentBudgetGroupedValues[dataKey][0]
          : undefined;

        if (!currentBudgetRow) {
          return budget;
        }

        const var_amount = round((currentBudgetRow?.current_lre || 0) - budget.current_lre, 2);

        const updatedSnapshotBudget = <ExtendedBudgetData>{
          ...budget,
          ...this.getMonthsSnapshotActuals(budget, currentBudgetRow),
        };

        const quarterAndYearsVariances = this.getVariances(
          this.calculateActuals(updatedSnapshotBudget),
          this.calculateActuals(currentBudgetRow),
          () => true
        );

        const forecastVariances = this.getVariances(
          updatedSnapshotBudget,
          currentBudgetRow,
          (key) => key.startsWith('EXPENSE_FORECAST') || key.startsWith('EXPENSE_FORECAST_USD')
        );

        const monthsVariances = this.getVariances(
          updatedSnapshotBudget,
          currentBudgetRow,
          (key) =>
            (key.startsWith('EXPENSE_WP::') || key.startsWith('EXPENSE_WP_USD::')) &&
            key !== 'EXPENSE_WP::TO_DATE'
        );

        return <ExtendedBudgetData>(<unknown>{
          ...currentBudgetRow,
          ...forecastVariances,
          ...monthsVariances,
          ...quarterAndYearsVariances,
          snapshot_lre: updatedSnapshotBudget.current_lre,
          var_amount,
          var_percent: this.budgetGridService.getVarPercent(
            currentBudgetRow?.current_lre || 0,
            var_amount,
            updatedSnapshotBudget.current_lre || 0
          ),
        });
      });
  }

  private getSnapshotInputs(snapshotName: string) {
    const selectedVendor = this.vendorQuery.getActive();

    const versions = this.budgetVersions$
      .getValue()
      .filter(
        ({ budget_name, vendor_id }) =>
          budget_name === snapshotName && (selectedVendor ? selectedVendor.id === vendor_id : true)
      )
      .sort((v1, v2) =>
        Utils.alphaNumSort(
          this.vendorQuery.getEntity(v1.vendor_id)?.name as string,
          this.vendorQuery.getEntity(v2.vendor_id)?.name as string
        )
      );

    const commonInputs = {
      budget_type: BudgetType.BUDGET_SNAPSHOT,
      in_month: false,
    };

    return versions.map<BudgetViewInput>((budgetVersion) => {
      return {
        ...commonInputs,
        budget_version_id: budgetVersion.budget_version_id,
        vendor_id: selectedVendor ? budgetVersion.vendor_id : null,
      };
    });
  }

  getSnapshotList() {
    return this.mainQuery.select('trialKey').pipe(this.getSnapshotListPipeline());
  }

  getSnapshotListPipeline() {
    return pipe(
      switchMap(() => {
        this.loading$.next(true);

        return this.gqlService.listBudgetVersions$([BudgetType.BUDGET_SNAPSHOT]);
      }),
      map(({ data, errors, success }) => {
        this.loading$.next(false);

        if (success && data) {
          this.budgetVersions$.next(data);

          return data;
        }

        if (errors.length) {
          this.overlayService.error(errors);
        }

        return [];
      })
    );
  }

  getSnapShotVersions() {
    return this.vendorQuery.selectActive().pipe(
      switchMap((vendorId) => {
        return this.budgetVersions$.pipe(
          map((versionList) => {
            const list = vendorId?.id ? versionList : uniqBy(versionList, 'budget_name');

            return list
              .filter((budgetVersion) => {
                return vendorId?.id ? budgetVersion.vendor_id === vendorId?.id : true;
              })
              .sort((snap1, snap2) => {
                if (
                  snap1.snapshot_type === BudgetSnapshotType.BUDGET_SNAPSHOT_USER_CREATED &&
                  snap2.snapshot_type !== BudgetSnapshotType.BUDGET_SNAPSHOT_USER_CREATED
                )
                  return -1;

                if (
                  snap1.snapshot_type !== BudgetSnapshotType.BUDGET_SNAPSHOT_USER_CREATED &&
                  snap2.snapshot_type === BudgetSnapshotType.BUDGET_SNAPSHOT_USER_CREATED
                )
                  return 1;

                return Utils.dateSort(snap2.create_date, snap1.create_date);
              })
              .map<Option>(({ budget_name, budget_snapshot_id, create_date, snapshot_type }) => ({
                create_date,
                id: budget_snapshot_id,
                label: budget_name,
                snapshot_type,
                value: budget_name,
              }));
          })
        );
      })
    );
  }

  private getVariances = (
    budget: ExtendedBudgetData,
    currentBudget: ExtendedBudgetData,
    findKey: (key: string) => boolean
  ) => {
    const data = pickBy({ ...currentBudget, ...budget }, (_, key) => findKey(key));

    return Object.keys(data).reduce((accum, key) => {
      const safeValue = <number>budget[key] || 0;
      const currentBudgetValue = <number>currentBudget[key] || 0;

      const varCost = currentBudgetValue - safeValue;

      const varPerc = safeValue ? round(varCost / safeValue, 2) : 0;

      return {
        ...accum,
        [`${key}::SNAPSHOT`]: safeValue,
        [`${key}::VAR_COST::SNAPSHOT`]: varCost,
        [`${key}::VAR_PERC::SNAPSHOT`]: varPerc,
      };
    }, {});
  };

  private getVendorIdList(budgetGridData: BudgetDataArrayType | ExtendedBudgetData[]) {
    return uniqBy(budgetGridData, 'vendor_id')
      .map(({ vendor_id }) => vendor_id)
      .filter((id) => !!id)
      .sort((vendor_id, vendor_id2) =>
        Utils.alphaNumSort(
          this.vendorQuery.getEntity(vendor_id)?.name as string,
          this.vendorQuery.getEntity(vendor_id2)?.name as string
        )
      ) as string[];
  }

  async removeBudgetSnapshots(snapshot: {
    create_date: string;
    id: string;
    label: string;
    snapshot_type: string;
    value: string;
  }) {
    const response = await firstValueFrom(this.gqlService.removeBudgetSnapshot$(snapshot.id));

    if (response.success) {
      this.budgetVersions$.next(
        this.budgetVersions$
          .getValue()
          .filter(
            ({ budget_version_id }) =>
              !response.data?.budget_version_ids.includes(budget_version_id)
          )
      );

      this.overlayService.success('Snapshot successfully removed!');
    } else {
      this.overlayService.error(response.errors);
    }
  }

  private resetGridRow(row: ExtendedBudgetData) {
    return Object.keys(row).reduce((accum, key) => {
      return <ExtendedBudgetData>{
        ...accum,
        expenses: [],
        [key]: NOT_RESETTABLE_GRID_KEYS.indexOf(key) !== -1 ? row[key] : 0,
      };
    }, row);
  }

  private resetInvalidBudgets(
    gridData: ExtendedBudgetData[],
    noDataVendorIds: string[]
  ): ExtendedBudgetData[] {
    return gridData.map((row) => {
      if (noDataVendorIds.includes(row.vendor_id || '')) {
        return this.resetGridRow(row);
      }

      return row;
    });
  }

  setOriginalBudgetData() {
    const budgetGridData = this.budgetStore.getValue().current_data;

    this.budgetStore.update({
      budget_data: budgetGridData,
    });
  }

  async updateSnapshots(
    snapshot: {
      create_date: string;
      id: string;
      label: string;
      snapshot_type: string;
      value: string;
    },
    updatedName: string
  ) {
    const snapshotInput = { budget_name: updatedName, id: snapshot.id };
    const response = await firstValueFrom(this.gqlService.updateBudgetSnapshot$(snapshotInput));

    if (response.success) {
      this.budgetVersions$.next(
        this.budgetVersions$.getValue().map((budgetVersion) => {
          const { budget_name, ...restData } = budgetVersion;

          return budget_name === snapshot.label
            ? { ...restData, budget_name: updatedName }
            : budgetVersion;
        })
      );

      this.overlayService.success('Snapshot successfully updated!');
    } else {
      this.overlayService.error(response.errors);
    }
  }
}
